import { useEffect, useRef, useState } from "react";
import type { PDFDocumentProxy, RenderTask } from "pdfjs-dist";

type PdfPageCanvasProps = {
  doc: PDFDocumentProxy;
  pageNumber: number;
  scale: number;
  rotation: number;
  onFirstPageSize?: (width: number, height: number) => void;
  onVisibilityChange?: (pageNumber: number, ratio: number) => void;
  registerPageRef?: (pageNumber: number, el: HTMLDivElement | null) => void;
};

export const PdfPageCanvas = ({
  doc,
  pageNumber,
  scale,
  rotation,
  onFirstPageSize,
  onVisibilityChange,
  registerPageRef,
}: PdfPageCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const renderTaskRef = useRef<RenderTask | null>(null);
  const [isVisible, setIsVisible] = useState(pageNumber === 1);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
        onVisibilityChange?.(pageNumber, entry.intersectionRatio);
      },
      { rootMargin: "600px 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber]);

  useEffect(() => {
    if (!isVisible) return;
    let cancelled = false;

    const render = async () => {
      const page = await doc.getPage(pageNumber);
      if (cancelled) return;

      const viewport = page.getViewport({ scale, rotation });
      const canvas = canvasRef.current;
      if (!canvas) return;

      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;

      const context = canvas.getContext("2d");
      if (!context) return;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);

      renderTaskRef.current?.cancel();
      const task = page.render({ canvasContext: context, viewport });
      renderTaskRef.current = task;

      try {
        await task.promise;
        if (cancelled) return;
        setSize({ width: viewport.width, height: viewport.height });
        if (pageNumber === 1) onFirstPageSize?.(viewport.width, viewport.height);
      } catch (err: any) {
        if (err?.name !== "RenderingCancelledException") {
          console.error(`Failed to render PDF page ${pageNumber}`, err);
        }
      }
    };

    render();

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
    };
  }, [doc, pageNumber, scale, rotation, isVisible]);

  useEffect(() => {
    registerPageRef?.(pageNumber, containerRef.current);
    return () => registerPageRef?.(pageNumber, null);
  }, [pageNumber, registerPageRef]);

  return (
    <div
      ref={containerRef}
      className="pdf-page"
      style={{
        width: size.width || undefined,
        height: size.height || 400,
      }}
    >
      {isVisible ? (
        <canvas ref={canvasRef} />
      ) : (
        <div className="pdf-page-placeholder">Page {pageNumber}</div>
      )}
    </div>
  );
};